import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { App, Button, Input } from 'antd';
import { fetchSegmentTranslationPrompt, updateSegmentTranslationPrompt } from 'api/events';
import { useI18n } from 'components/I18nProvider';
import { getLocalizedLanguageName } from 'lib/language-display';
import styles from './style.module.scss';
const TranslationPromptEditor = ({ eventId, segmentId, language }) => {
    const { intl } = useI18n();
    const { message } = App.useApp();
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState('');
    const [editContext, setEditContext] = useState(null);
    const contextStale = editContext !== null && (editContext.segmentId !== segmentId
        || editContext.language !== language);
    const collapse = () => {
        setExpanded(false);
        setDraft('');
        setEditContext(null);
    };
    useEffect(() => {
        if (!expanded || editContext === null) {
            return;
        }
        if (editContext.segmentId !== segmentId || editContext.language !== language) {
            setExpanded(false);
            setDraft('');
            setEditContext(null);
        }
    }, [segmentId, language, expanded, editContext]);
    const handleEdit = async () => {
        setLoading(true);
        try {
            const customPrompt = await fetchSegmentTranslationPrompt(eventId, segmentId, language);
            setDraft(customPrompt ?? '');
            setEditContext({ segmentId, language });
            setExpanded(true);
        }
        catch {
            void message.error(intl.formatMessage({ id: 'translationPromptLoadFailed' }));
        }
        finally {
            setLoading(false);
        }
    };
    const handleSave = async () => {
        if (editContext === null || contextStale) {
            return;
        }
        setSaving(true);
        try {
            await updateSegmentTranslationPrompt(eventId, editContext.segmentId, editContext.language, draft.trim() || null);
            collapse();
        }
        catch {
            void message.error(intl.formatMessage({ id: 'translationPromptSaveFailed' }));
        }
        finally {
            setSaving(false);
        }
    };
    const saveLanguageLabel = editContext !== null
        ? getLocalizedLanguageName(intl, editContext.language)
        : getLocalizedLanguageName(intl, language);
    return (_jsxs("div", { className: styles.promptEditor, children: [!expanded && (_jsx(Button, { "test-id": "admin-subtitles-preview-edit-prompt-button", loading: loading, onClick: () => void handleEdit(), children: intl.formatMessage({ id: 'editTranslationPrompt' }) })), expanded && editContext !== null && (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.promptEditorActions, children: [_jsx(Button, { "test-id": "admin-subtitles-preview-save-prompt-button", type: "primary", loading: saving, disabled: contextStale, onClick: () => void handleSave(), children: intl.formatMessage({ id: 'saveTranslationPrompt' }, { language: saveLanguageLabel }) }), _jsx(Button, { "test-id": "admin-subtitles-preview-cancel-prompt-button", disabled: saving, onClick: collapse, children: intl.formatMessage({ id: 'cancel' }) })] }), _jsx(Input.TextArea, { "test-id": "admin-subtitles-preview-prompt-textarea", className: styles.promptTextarea, value: draft, onChange: (e) => setDraft(e.target.value), disabled: saving || contextStale, autoSize: { minRows: 4, maxRows: 20 } })] }))] }));
};
export default TranslationPromptEditor;
